import * as React from "react";

export type ListItem = {
    value: string;
    label: string;
    sublabel?: string;
};

type ListSelectProps = {
    items: ListItem[];
    value?: string;
    onChange: (value: string) => void;
    searchable?: boolean;
    placeholder?: string;
    emptyText?: string;
    className?: string;
};

export default function ListSelect({
    items,
    value,
    onChange,
    searchable = true,
    placeholder = "Search…",
    emptyText = "No items",
    className,
}: ListSelectProps) {
    const [q, setQ] = React.useState("");
    const ref = React.useRef<HTMLDivElement>(null);

    const filtered = React.useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return items;
        return items.filter((it) => {
            const l = it.label.toLowerCase();
            const sub = (it.sublabel ?? "").toLowerCase();
            return l.includes(s) || sub.includes(s) || it.value.toLowerCase().includes(s);
        });
    }, [items, q]);

    // keyboard nav (↑/↓/Enter)
    const [activeIdx, setActiveIdx] = React.useState(0);
    React.useEffect(() => setActiveIdx(0), [q, items.length]);

    const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
        if (!filtered.length) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
            scrollIntoView(activeIdx + 1);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIdx((i) => Math.max(i - 1, 0));
            scrollIntoView(activeIdx - 1);
        } else if (e.key === "Enter") {
            e.preventDefault();
            const v = filtered[activeIdx]?.value;
            if (v) onChange(v);
        }
    };

    const scrollIntoView = (idx: number) => {
        const el = ref.current?.querySelectorAll<HTMLElement>(".mc-ls-item")[idx];
        el?.scrollIntoView({ block: "nearest" });
    };

    return (
        <div className={`mc-ls ${className || ""}`} onKeyDown={onKeyDown}>
            {searchable && (
                <div className="mc-ls-search">
                    <input
                        className="mc-ls-search-input"
                        placeholder={placeholder}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                </div>
            )}

            <div className="mc-ls-viewport" ref={ref} role="listbox" aria-activedescendant={filtered[activeIdx]?.value}>
                {filtered.length === 0 ? (
                    <div className="mc-ls-empty">{emptyText}</div>
                ) : (
                    filtered.map((it, i) => {
                        const selected = it.value === value;
                        const active = i === activeIdx;
                        return (
                            <button
                                type="button"
                                key={it.value}
                                className={`mc-ls-item ${selected ? "is-selected" : ""} ${active ? "is-active" : ""}`}
                                onClick={() => onChange(it.value)}
                            >
                                <div className="mc-ls-label">{it.label}</div>
                                {it.sublabel && <div className="mc-ls-sublabel">{it.sublabel}</div>}
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
}
